/**
 * blameGutter — CM6 gutter extension showing blame annotations.
 *
 * Displays author + relative time for each line-group.
 * Local edits show "You · unsaved" in amber.
 * Continuation lines show "│".
 *
 * Line-height is managed entirely by CM6 — no manual measurement needed.
 */
import { gutter, GutterMarker } from '@codemirror/view'
import { StateField, StateEffect, type Extension } from '@codemirror/state'
import type { EditorView } from '@codemirror/view'
import type { BlameLine } from '../engine/types'
import { writerColor, timeAgo } from '../utils/writerUtils'

// ── Types ──────────────────────────────────────────────────────

interface LocalBlameLine extends BlameLine {
    local: true
}

function isLocal(line: BlameLine): line is LocalBlameLine {
    return !!(line as LocalBlameLine).local
}

// ── State management ───────────────────────────────────────────

/** Effect to push new blame data into the editor state */
export const setBlameLinesEffect = StateEffect.define<{
    lines: BlameLine[]
    enabled: boolean
    stale: boolean
}>()

interface BlameState {
    lines: BlameLine[]
    enabled: boolean
    stale: boolean
}

const blameStateField = StateField.define<BlameState>({
    create() {
        return { lines: [], enabled: false, stale: false }
    },
    update(state, tr) {
        for (const effect of tr.effects) {
            if (effect.is(setBlameLinesEffect)) {
                return effect.value
            }
        }
        return state
    },
})

// ── Gutter markers ─────────────────────────────────────────────

class BlameGroupStartMarker extends GutterMarker {
    constructor(
        private name: string,
        private time: string,
        private color: string,
        private isLocalEdit: boolean,
        private compact: boolean = false,
    ) { super() }

    toDOM(): Node {
        const wrap = document.createElement('div')
        wrap.className = `blame-line blame-group-start${this.isLocalEdit ? ' blame-local' : ''}${this.compact ? ' blame-compact' : ''}`
        wrap.style.setProperty('--writer-color', this.color)
        wrap.title = this.isLocalEdit
            ? 'Unsaved local edit'
            : `${this.name} · ${this.time}`

        if (this.compact) {
            // Ultra-compact: color dot + short time only
            const dot = document.createElement('span')
            dot.className = 'blame-dot'
            dot.textContent = '●'
            const time = document.createElement('span')
            time.className = 'blame-time'
            time.textContent = this.time
            wrap.append(dot, time)
        } else {
            const author = document.createElement('span')
            author.className = 'blame-author'
            author.textContent = this.name
            const time = document.createElement('span')
            time.className = 'blame-time'
            time.textContent = this.time
            wrap.append(author, time)
        }
        return wrap
    }
}

class BlameContinuationMarker extends GutterMarker {
    constructor(private isLocalEdit: boolean) { super() }

    toDOM(): Node {
        const wrap = document.createElement('div')
        wrap.className = `blame-line${this.isLocalEdit ? ' blame-local' : ''}`
        const cont = document.createElement('span')
        cont.className = 'blame-continuation'
        cont.textContent = '│'
        wrap.append(cont)
        return wrap
    }
}

// ── Gutter extension ───────────────────────────────────────────

function blameGutterExtension(opts: { compact?: boolean } = {}): Extension {
    const compact = opts.compact ?? false
    return gutter({
        class: 'sb-blame-gutter',
        lineMarker(view: EditorView, line) {
            const state = view.state.field(blameStateField)
            if (!state.enabled || state.lines.length === 0) return null

            // Map editor line number → blame line index
            const doc = view.state.doc
            const lineNo = doc.lineAt(line.from).number  // 1-indexed
            const idx = lineNo - 1

            if (idx < 0 || idx >= state.lines.length) return null

            const blameLine = state.lines[idx]
            const prevLine = idx > 0 ? state.lines[idx - 1] : null
            const local = isLocal(blameLine)

            // Detect group boundary
            const isNewGroup = idx === 0
                || (prevLine && isLocal(prevLine) !== local)
                || (!local && prevLine && prevLine.commitHash !== blameLine.commitHash)

            if (isNewGroup) {
                const name = local ? 'You' : (blameLine.writerName || blameLine.writerKid?.slice(0, 5) || '?')
                const time = local ? 'unsaved' : timeAgo(blameLine.timestamp ?? null)
                const color = local ? 'var(--color-warning, #f59e0b)' : writerColor(blameLine.writerKid)
                return new BlameGroupStartMarker(name, time, color, local, compact)
            }

            return new BlameContinuationMarker(local)
        },
        lineMarkerChange(update) {
            return update.transactions.some(tr =>
                tr.effects.some(e => e.is(setBlameLinesEffect))
            )
        },
    })
}

// ── Public API ──────────────────────────────────────────────────

/**
 * Returns the blame gutter extension array.
 * Use `setBlameLinesEffect` to update blame data from React.
 * @param opts.compact — If true, renders ultra-compact mode (dot + time only)
 */
export function blameGutter(opts: { compact?: boolean } = {}): Extension {
    return [
        blameStateField,
        blameGutterExtension(opts),
    ]
}
